import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { pageCountOf, useClampPage, usePageParam } from '@shared/pagination';
import { formatDate, toMachineDate } from '@shared/format';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { Pager } from '@shared/ui/pager';

/**
 * How many pages the total divides into, or `undefined` when the service
 * declared a page size of zero — a figure the console must not divide by, and
 * one it must not silently replace with a guess either.
 */
import {
  formatDuration,
  formatEpisodeCount,
  formatSeasonCount,
  hasSeasons,
  resolveContentType,
  resolvePublishState,
  resolveRowActions,
} from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import { CONTENT_TYPES } from '../_shared/catalog-settings';
import type { ContentListRow } from '../_shared/types';
import { ListSearch } from '@shared/ui/ListSearch';

import { ContentFacets } from './ContentList.Facets';
import { copy } from './copy';
import { errors } from './errors';
import { usePublishContentItem, useUnpublishContentItem } from './mutations';
import { adminItemsKey, useAdminContentItems } from './queries';

const TABLE_COLUMNS = 6;

/**
 * `/content` — every title in every publish state.
 *
 * This is the one surface in the console that lists titles a viewer cannot see:
 * a draft does not appear in public search at all.
 *
 * **Every publish transition lives on this list and nowhere else.** Neither the
 * authoring form nor the edit surface carries a state control — one decision,
 * one place, one copy of the confirmation wording. The edit surface shows the
 * state and links back here instead of dead-ending.
 *
 * **The row menu is derived from the row's own state**, never authored per row:
 * a menu offering «انشر» beside a badge reading «منشور» describes a state the
 * operator can see is false. The wording follows the state too — «أعد النشر» on
 * an unpublished title says something «انشر الآن» does not.
 *
 * What the surface deliberately does not carry: **no pagination or load-more**
 * (load-more is scoped to grids and pagination to the inbox, and this list
 * states its own truncation instead), **no delete** (the title is unpublished or
 * edited here; removing assets and seasons lives on the assets surface), **no
 * row selection or bulk action** (both are inbox rows), **no column sort** (the
 * list is not an ordered field the way a shelf list is), and **no top-bar search
 * field** — the console withholds that one at every tier, and this list carries
 * its own beside the rows it narrows instead, alongside the publish-state and
 * type facets.
 */
export function ContentListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Facet state lives in the URL, so a filtered view is a link an operator can
  // keep and a reload lands on what they were looking at.
  const state = searchParams.get('state');
  const contentType = searchParams.get('type');
  const term = searchParams.get('q') ?? '';

  // The ordinal is read from the URL and clamped to a page that exists — see
  // the shared hook. `/content?page=999` used to draw «صفحة ٩٩٩ من ٥» over page
  // one's rows, which is a location that does not exist presented as a real one.
  const { page, goToPage } = usePageParam();

  const filter = { state, contentType, term, page };
  const listQuery = useAdminContentItems(filter);

  const totalPages = listQuery.data ? pageCountOf(listQuery.data.total, listQuery.data.page_size) : undefined;
  useClampPage(page, totalPages);

  const publishItem = usePublishContentItem();
  const unpublishItem = useUnpublishContentItem();

  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingUnpublish, setPendingUnpublish] = useState<ContentListRow | null>(null);

  const rows = listQuery.data?.items ?? [];
  const filtersActive = state !== null || contentType !== null || term.trim().length > 0;

  /**
   * The term rides in the URL like the facets do, on `replace` rather than
   * `push`: typing is one continuous act, and a history entry per keystroke
   * turns Back into a character-by-character undo of the search box.
   */
  function setTerm(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function setFacet(name: 'state' | 'type', value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(name);
    else next.set(name, value);
    // A new facet is a new list; page four of «الكلّ» is not page four of
    // «مسودّة», so the ordinal does not ride across.
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  /** Page one is the absence of the parameter, never `?page=1` written out. */
  function failureMessage(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;

    // The publish refusal names its outstanding fields; repeating one sentence
    // would leave the operator to go hunting for which of the seven is missing.
    if (code === 'CONTENT_REQUIRED_METADATA_MISSING' && error instanceof ApiError) {
      const fields = (error.details ?? []).map((detail) => detail.field ?? detail.code).join(' · ');
      return fields
        ? `${copy.toast.missingMetadata} ${fields}`
        : resolveErrorCopy(code, status, errors).message;
    }

    return resolveErrorCopy(code, status, errors).message;
  }

  function onTransitionError(error: unknown) {
    toast.show(failureMessage(error), 'danger');
  }

  function publish(row: ContentListRow) {
    publishItem.mutate(row.id, {
      onSuccess: () => toast.show(copy.toast.published(row.title), 'success'),
      onError: onTransitionError,
    });
  }

  function askUnpublish(row: ContentListRow) {
    setPendingUnpublish(row);
    setConfirmSubject({
      heading: copy.unpublish.heading(row.title),
      body: copy.unpublish.body,
      commitLabel: copy.unpublish.commit,
      // Reversible-but-consequential: publishing brings all of it back, and the
      // body says so rather than borrowing a removal's finality.
      intent: 'warning',
    });
  }

  function commitUnpublish() {
    const row = pendingUnpublish;
    if (!row) return;

    unpublishItem.mutate(row.id, {
      onSuccess: () => {
        setConfirmSubject(null);
        setPendingUnpublish(null);
        toast.show(copy.toast.unpublished(row.title), 'success');
      },
      onError: (error) => {
        setConfirmSubject(null);
        setPendingUnpublish(null);
        onTransitionError(error);
      },
    });
  }

  function menuEntries(row: ContentListRow): RowMenuEntry[] {
    const actions = resolveRowActions(row.state);
    const entries: RowMenuEntry[] = [
      {
        key: `${row.id}:edit`,
        label: copy.rowMenu.edit,
        // Client-side navigation: a full document load here would discard the
        // query cache and the session slice the shell holds in memory.
        onSelect: () => void navigate(`/content/${row.id}/edit`),
      },
      {
        key: `${row.id}:assets`,
        label: hasSeasons(row.content_type) ? copy.rowMenu.assetsSeries : copy.rowMenu.assetsFilm,
        onSelect: () => void navigate(`/content/${row.id}/assets`),
      },
    ];

    if (actions.publish) {
      // The service refuses a publication with nothing to play, and this row
      // already carries that answer. Offering the press anyway spends a round
      // trip to be told what the list knew before the menu opened.
      const notReady = !row.has_playable_asset;

      entries.push({
        key: `${row.id}:publish`,
        label: actions.publish === 'republish' ? copy.rowMenu.republish : copy.rowMenu.publish,
        disabled: notReady,
        onSelect: () => publish(row),
      });
    }

    if (actions.unpublish) {
      entries.push({
        key: `${row.id}:unpublish`,
        label: copy.rowMenu.unpublish,
        danger: true,
        onSelect: () => askUnpublish(row),
      });
    }

    return entries;
  }

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <ListSearch
        label={copy.search.label}
        placeholder={copy.search.placeholder}
        hint={copy.search.hint}
        value={term}
        onChange={setTerm}
      />

      <ContentFacets
        contentTypes={CONTENT_TYPES}
        state={state}
        contentType={contentType}
        onState={(next) => setFacet('state', next)}
        onContentType={(next) => setFacet('type', next)}
      />

      <section aria-labelledby="content-list">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="content-list" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>

          <Link
            to="/content/new"
            className="hit-target ms-auto flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay"
          >
            <PlusGlyph />
            {copy.section.add}
          </Link>
        </div>

        {listQuery.isError ? (
          <SurfaceError
            error={listQuery.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: adminItemsKey(filter) })}
          />
        ) : null}

        {listQuery.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <ContentTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </ContentTable>
          </>
        ) : null}

        {listQuery.data ? (
          rows.length === 0 ? (
            <EmptyState
              message={
                page > 1
                  ? copy.states.emptyPage
                  : filtersActive
                    ? copy.states.emptyFiltered
                    : copy.states.empty
              }
              action={
                filtersActive ? (
                  <button
                    type="button"
                    onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                    className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
                  >
                    {copy.states.clearFilters}
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <ContentTable>
                {rows.map((row) => (
                  <ContentRow
                    key={row.id}
                    row={row}
                    entries={menuEntries(row)}
                    menuHint={publishNotReady(row) ? copy.rowMenu.publishNotReadyHint : undefined}
                  />
                ))}
              </ContentTable>

              <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">{copy.truncationTail}</p>
            </>
          )
        ) : null}

        {/*
          Outside the row check on purpose: a page walked one past the end has no
          rows and the control that walks back is the only way off it.
        */}
        {listQuery.data ? (
          <Pager
            label={copy.pager.label}
            page={page}
            pageCount={totalPages}
            shown={rows.length}
            total={listQuery.data.total}
            noun={{
              one: copy.pager.shownOne,
              two: copy.pager.shownTwo,
              few: copy.pager.shownFew,
              many: copy.pager.shownMany,
            }}
            hasPrevious={page > 1}
            hasNext={listQuery.data.has_next}
            onPrevious={() => goToPage(page - 1)}
            onNext={() => goToPage(page + 1)}
            onGoToPage={goToPage}
          />
        ) : null}
      </section>

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={unpublishItem.isPending}
        onConfirm={commitUnpublish}
        onCancel={() => {
          setConfirmSubject(null);
          setPendingUnpublish(null);
        }}
      />
    </>
  );
}

/**
 * Whether a subscriber can reach this row at all.
 *
 * Only a published title can be hidden this way: a draft is invisible because it
 * is a draft, which the state badge already says, and a second badge repeating
 * it would be noise on every unpublished row in the table.
 */
function isHiddenFromSubscribers(row: ContentListRow): boolean {
  return row.state === 'PUBLISHED' && !row.has_playable_asset;
}

/** Whether this row's publish entry is the disabled one, and the hint is owed. */
function publishNotReady(row: ContentListRow): boolean {
  return resolveRowActions(row.state).publish !== null && !row.has_playable_asset;
}

function ContentRow({
  row,
  entries,
  menuHint,
}: {
  row: ContentListRow;
  entries: RowMenuEntry[];
  /** The reason the publish entry is disabled; `undefined` leaves the menu bare. */
  menuHint?: string | undefined;
}) {
  const state = resolvePublishState(row.state);
  const shape = shapeLine(row);

  return (
    <tr>
      <DTableRowHeader>
        <span className="block">{row.title}</span>
        {shape ? <span className="mt-1 block text-xs text-ink-muted">{shape}</span> : null}
      </DTableRowHeader>

      <td>
        <StateBadge kind="neutral">{resolveContentType(row.content_type)}</StateBadge>
      </td>

      {/*
        The state cell carries its own column hook. A row holds two badges and
        the type comes first, so every handler that reached for «the first badge
        with a colour class» wrote the state over the type — a colour class says
        how a thing looks, not what it is.
      */}
      {/*
        The hidden badge sits BESIDE the state, never instead of it. The record
        is published — replacing the state would misreport the row — but a
        published title whose media has not converted is unreachable to every
        subscriber, and the console is the only surface that can say so.
      */}
      <td data-column="state">
        <StateBadge kind={state.kind}>{state.label}</StateBadge>
        {isHiddenFromSubscribers(row) ? (
          <StateBadge className="mt-1" kind="warning">
            {copy.notVisibleYet}
          </StateBadge>
        ) : null}
      </td>

      <td className={row.category_name ? undefined : 'text-ink-muted'}>
        {row.category_name ?? '—'}
        {row.category_name ? null : <span className="sr-only">{copy.noCategory}</span>}
      </td>

      {/*
        A date the record does not hold is stated, never fabricated: a draft may
        be written and never dated, and a surface that invented one here would
        be asserting a publication that has not happened.
      */}
      <td className="whitespace-nowrap">
        {row.dated_at ? (
          <time dateTime={toMachineDate(row.dated_at)}>
            <bdi>{formatDate(row.dated_at)}</bdi>
          </time>
        ) : (
          <span className="text-ink-muted">
            —<span className="sr-only">{copy.noDate}</span>
          </span>
        )}
      </td>

      <td className="w-[3.5rem]">
        <RowMenu label={copy.rowMenu.label(row.title)} entries={entries} hint={menuHint} />
      </td>
    </tr>
  );
}

/**
 * The line under a title's name.
 *
 * A series counts its seasons and episodes; **a film counts neither**, because
 * it has zero groups and counting to «٠ مواسم» would draw the synthetic parent
 * the domain model forbids. It states its runtime instead — and a film whose
 * runtime is not known yet says NOTHING, rather than «حلقة واحدة» about a thing
 * that has no episodes.
 */
function shapeLine(row: ContentListRow): string | null {
  if (hasSeasons(row.content_type)) {
    return `${formatSeasonCount(row.group_count ?? 0)} · ${formatEpisodeCount(row.asset_count)}`;
  }

  return formatDuration(row.duration_ms);
}

function ContentTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="44rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.title}</th>
            <th scope="col">{copy.columns.type}</th>
            <th scope="col">{copy.columns.state}</th>
            <th scope="col">{copy.columns.category}</th>
            <th scope="col">{copy.columns.date}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}

function PlusGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
